import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useEsriMapContext } from '../EsriMapContext';
import { useFilters } from './useFilters';
import {
	polygonFilterFetch,
	clearPolygonHighlights,
	selectPolygonHighlights,
} from './polygonHighlight/polygonHighlightSlice';
import {
	selectSelectedSystemType,
	selectSelectedAssetType,
} from './useFilters/filterSlice';

export const usePolygonFilter = () => {
	const dispatch = useDispatch();
	const { layerSelectionApi } = useEsriMapContext();
	const { polygonFilter } = useFilters();
	const highlights = useSelector(selectPolygonHighlights);
	const selectedSystemType = useSelector(selectSelectedSystemType);
	const selectedAssetType = useSelector(selectSelectedAssetType);

	// Tracks which layerIds this hook last applied highlights to, so when the
	// polygon highlights clear we can release only those layers instead of
	// nuking every layer's selection (which would erase useSelection-driven
	// highlights — e.g. the inset map showing a selected asset in details).
	const lastAppliedLayersRef = useRef<string[]>([]);

	// Dispatch fetch or clear when polygon changes
	useEffect(() => {
		if (!layerSelectionApi || !polygonFilter) {
			dispatch(clearPolygonHighlights());
			return;
		}
		if (!selectedSystemType || !selectedAssetType) return;

		const matchingLayer = layerSelectionApi
			.getQueryableLayers()
			.find(
				l =>
					l.systemType === selectedSystemType &&
					l.assetType === selectedAssetType,
			);

		if (matchingLayer) {
			dispatch(
				polygonFilterFetch({ polygon: polygonFilter, layers: [matchingLayer] }),
			);
		}
	}, [
		polygonFilter,
		layerSelectionApi,
		selectedSystemType,
		selectedAssetType,
		dispatch,
	]);

	// Apply highlight results to map layers synchronously
	useEffect(() => {
		if (!layerSelectionApi) return;
		const entries = Object.entries(highlights);
		if (entries.length === 0) {
			// Release only the layers this hook previously highlighted. Avoid
			// calling layerSelectionApi.clearSelection() here because it wipes
			// every layer's selection — including selections owned by other
			// hooks (e.g. useSelection driving the details inset map highlight).
			if (lastAppliedLayersRef.current.length > 0) {
				lastAppliedLayersRef.current.forEach(layerId => {
					layerSelectionApi.setSelectedItems(layerId, []);
				});
				lastAppliedLayersRef.current = [];
			}
			return;
		}
		entries.forEach(([layerId, ids]) => {
			layerSelectionApi.setSelectedItems(layerId, ids);
		});
		lastAppliedLayersRef.current = entries.map(([layerId]) => layerId);
	}, [highlights, layerSelectionApi]);
};
